// 2026-11-05 (feedback pipeline, docs/product/feedback-pipeline.md): the web's side of the api's pipeline routes.
//   drafts           POST feedback/:id/send {text?}, DELETE feedback/:id     (DraftCard)
//   Your reports     GET me/reports                                         (Settings > Help)
//   Fixed card       GET me/notices?unseen=1, POST me/notices/seen {ids}    (Home)
//   What's new       GET changelog (public)                                 (/whats-new)
//   Admin > Requests GET admin/requests?status=, POST admin/requests/:id/decision {decision, note?}
//   Admin > Features GET admin/features, POST admin/features/:key {state}
//   Admin > People   POST admin/users/:id/trusted {trusted}
// The norm* functions are pure (unit-tested in pipeline.test.ts).
import { call } from "./api";

const obj = (v: unknown): Record<string, unknown> => (v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : {});
const arr = (v: unknown, key: string): Record<string, unknown>[] => (Array.isArray(obj(v)[key]) ? (obj(v)[key] as unknown[]).map(obj) : []);
const s = (v: unknown) => (typeof v === "string" ? v : "");
const sOrNull = (v: unknown) => (typeof v === "string" && v ? v : null);

// ------------------------------------------------------------------ Your reports

export type PublicStatus = "received" | "looking" | "fixed" | "not_planned" | "closed";
export const STATUS_LABEL: Record<PublicStatus, string> = { received: "Received", looking: "Being looked at", fixed: "Fixed", not_planned: "Not planned", closed: "Closed" };
/** The status chip's tone (the app's .status-* classes). */
export const STATUS_TONE: Record<PublicStatus, string> = { received: "idle", looking: "busy", fixed: "done", not_planned: "bad", closed: "idle" };
const isStatus = (v: unknown): v is PublicStatus => typeof v === "string" && v in STATUS_LABEL;

export type MyReport = { id: string; kind: string; source: "user" | "auto"; text: string; status: PublicStatus; note: string | null; at: string };
export function normReports(raw: unknown): MyReport[] {
  return arr(raw, "reports").flatMap((o) =>
    typeof o.id === "string" && typeof o.text === "string"
      ? [{ id: o.id, kind: s(o.kind) || "problem", source: o.source === "auto" ? ("auto" as const) : ("user" as const), text: o.text, status: isStatus(o.status) ? o.status : "received", note: sOrNull(o.note), at: s(o.at) }]
      : [],
  );
}
export const myReports = async () => normReports(await call("GET", "me/reports"));

// ------------------------------------------------------------------ notices (the "Fixed since your last visit" card)

export type Notice = { id: string; kind: "got_it" | "looking" | "shipped" | "declined"; message: string; report: string | null; reason: string | null; at: string };
const NOTICE_KINDS = new Set(["got_it", "looking", "shipped", "declined"]);
export function normNotices(raw: unknown): Notice[] {
  return arr(raw, "notices").flatMap((o) =>
    typeof o.id === "string" && NOTICE_KINDS.has(o.kind as string) ? [{ id: o.id, kind: o.kind as Notice["kind"], message: s(o.message), report: sOrNull(o.report), reason: sOrNull(o.reason), at: s(o.at) }] : [],
  );
}
/** The Home card: shipped and declined notices only. */
export const fixedNotices = (n: Notice[]) => n.filter((x) => x.kind === "shipped" || x.kind === "declined");
/** "Your report, quoted short" (60 characters, cut at a word). */
export function quoteShort(text: string | null, max = 60): string {
  const t = (text ?? "").replace(/\s+/g, " ").trim();
  if (t.length <= max) return t;
  const cut = t.slice(0, max);
  return `${cut.slice(0, cut.lastIndexOf(" ") > max / 2 ? cut.lastIndexOf(" ") : max).trimEnd()}...`;
}
/** One line of the card: "Fixed" or "Not planned: <reason>". */
export const noticeOutcome = (n: Notice) => (n.kind === "shipped" ? "Fixed" : n.reason ? `Not planned: ${n.reason}` : "Not planned");
export const unseenNotices = async () => normNotices(await call("GET", "me/notices?unseen=1"));
export const markNoticesSeen = (ids: string[]) => call("POST", "me/notices/seen", { ids });

// ------------------------------------------------------------------ what's new

export type ChangelogItem = { date: string; line: string };
export function normChangelog(raw: unknown): ChangelogItem[] {
  return arr(raw, "items").flatMap((o) => (typeof o.line === "string" && o.line.trim() && typeof o.date === "string" ? [{ date: o.date, line: o.line.trim() }] : []));
}
/** The changelog grouped by day, newest first. */
export function byDay(items: ChangelogItem[]): { date: string; lines: string[] }[] {
  const out: { date: string; lines: string[] }[] = [];
  for (const it of [...items].sort((a, b) => b.date.localeCompare(a.date))) {
    const last = out[out.length - 1];
    if (last && last.date === it.date) last.lines.push(it.line);
    else out.push({ date: it.date, lines: [it.line] });
  }
  return out;
}
export const changelog = async () => normChangelog(await call("GET", "changelog"));

// ------------------------------------------------------------------ drafts

export const sendDraftNow = (id: string, text?: string | null) => call<{ id: string; text: string; already: boolean }>("POST", `feedback/${encodeURIComponent(id)}/send`, text ? { text } : {});
export const discardDraftNow = (id: string) => call("DELETE", `feedback/${encodeURIComponent(id)}`);

// ------------------------------------------------------------------ Admin > Requests

export type RequestFilter = "needs" | "progress" | "shipped" | "all";
export const REQUEST_FILTERS: { id: RequestFilter; label: string }[] = [
  { id: "needs", label: "Needs you" },
  { id: "progress", label: "In progress" },
  { id: "shipped", label: "Shipped" },
  { id: "all", label: "All" },
];
export type Reporter = { id: string; name: string | null; email: string | null; trusted: boolean };
export type FixRequest = {
  id: string;
  title: string;
  summary: string;
  label: string;
  status: string;
  reports: number;
  reporters: Reporter[];
  prNumber: number | null;
  prUrl: string | null;
  why: string | null;
  decision: "approved" | "rejected" | null;
  decisionNote: string | null;
  updatedAt: string;
};
export function normRequests(raw: unknown): { requests: FixRequest[]; counts: Record<RequestFilter, number> } {
  const c = obj(obj(raw).counts);
  const n = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : 0);
  return {
    counts: { needs: n(c.needs), progress: n(c.progress), shipped: n(c.shipped), all: n(c.all) },
    requests: arr(raw, "requests").flatMap((o) =>
      typeof o.id === "string"
        ? [
            {
              id: o.id,
              title: s(o.title) || "Untitled",
              summary: s(o.summary),
              label: s(o.label) || "pending",
              status: s(o.status) || "new",
              reports: n(o.reports),
              reporters: (Array.isArray(o.reporters) ? o.reporters : []).map(obj).flatMap((r) => (typeof r.id === "string" ? [{ id: r.id, name: sOrNull(r.name), email: sOrNull(r.email), trusted: r.trusted === true }] : [])),
              prNumber: typeof o.prNumber === "number" ? o.prNumber : null,
              // only links to the repo's own pull requests
              prUrl: typeof o.prUrl === "string" && /^https:\/\/github\.com\/thatdspguy\/cao-studio\/pull\/\d+$/.test(o.prUrl) ? o.prUrl : null,
              why: sOrNull(o.why),
              decision: o.decision === "approved" || o.decision === "rejected" ? o.decision : null,
              decisionNote: sOrNull(o.decisionNote),
              updatedAt: s(o.updatedAt),
            },
          ]
        : [],
    ),
  };
}
/** Statuses where Keaton's Approve / Reject applies. */
export const canDecide = (r: FixRequest) => r.status === "needs_keaton" || r.status === "awaiting_keaton";
export const STATUS_WORDS: Record<string, string> = {
  new: "New",
  held: "Waiting a few minutes",
  queued: "Queued",
  fixing: "Fixing",
  reviewing: "Reviewing",
  awaiting_keaton: "Fix ready, needs you",
  approved: "Approved",
  merging: "Merging",
  merged: "Merged",
  shipped: "Shipped",
  needs_keaton: "Needs your call",
  rejected: "Rejected",
  failed: "Failed",
  closed: "Closed",
  rolled_back: "Rolled back",
};
export const listRequests = async (f: RequestFilter) => normRequests(await call("GET", `admin/requests?status=${f}`));
export const decideRequest = (id: string, decision: "approved" | "rejected", note?: string) => call("POST", `admin/requests/${encodeURIComponent(id)}/decision`, { decision, ...(note?.trim() ? { note: note.trim() } : {}) });
export const setTrusted = (userId: string, trusted: boolean) => call<{ id: string; trusted: boolean }>("POST", `admin/users/${encodeURIComponent(userId)}/trusted`, { trusted });

// ------------------------------------------------------------------ Admin > Features

export type FlagState = "off" | "me" | "everyone";
export const FLAG_STATES: { id: FlagState; label: string }[] = [
  { id: "off", label: "Off" },
  { id: "me", label: "Just me" },
  { id: "everyone", label: "Everyone" },
];
export type Feature = { key: string; state: FlagState; title: string; description: string };
export function normFeatures(raw: unknown): Feature[] {
  return arr(raw, "features").flatMap((o) =>
    typeof o.key === "string" && (o.state === "off" || o.state === "me" || o.state === "everyone") ? [{ key: o.key, state: o.state as FlagState, title: s(o.title) || o.key, description: s(o.description) }] : [],
  );
}
export const listFeatures = async () => normFeatures(await call("GET", "admin/features"));
export const setFeature = (key: string, state: FlagState) => call("POST", `admin/features/${encodeURIComponent(key)}`, { state });
